import { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { streamChat } from "@/lib/api";
import { useApp } from "@/lib/i18n";
import { linkify } from "@/lib/linkify";
import { ChatLauncher } from "./ChatLauncher";

const sessionId = () => {
  let s = localStorage.getItem("minit_pub_chat");
  if (!s) { s = crypto.randomUUID().replace(/-/g, ""); localStorage.setItem("minit_pub_chat", s); }
  return s;
};

const PublicChat = () => {
  const { L } = useApp();
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  useEffect(() => { const box = endRef.current?.parentElement; if (box) box.scrollTop = box.scrollHeight; }, [messages]);
  const chips = [L("Berapa harga pelan Pro?", "How much is the Pro plan?"), L("Bagaimana MinitAI berfungsi?", "How does MinitAI work?"), L("Boleh guna dengan Zoom?", "Does it work with Zoom?"), L("Cara bayar langganan?", "How do I pay?")];
  const send = async (question) => {
    if (!question.trim() || loading) return;
    setQ(""); setLoading(true);
    setMessages((m) => [...m, { role: "user", content: question }, { role: "assistant", content: "" }]);
    const patch = (fn) => setMessages((m) => (m[m.length - 1]?.role === "assistant" ? [...m.slice(0, -1), fn(m[m.length - 1])] : m));
    try {
      await streamChat({ session_id: sessionId(), question }, { onDelta: (d) => patch((l) => ({ ...l, content: l.content + d })), onError: (e) => patch((l) => ({ ...l, content: l.content + e })) }, "/public/chat");
    } catch (e) { patch((l) => ({ ...l, content: e.message })); }
    setLoading(false);
  };
  return (
    <div className="flex h-[460px] flex-col" data-testid="public-chat">
      <div className="slim-scroll flex-1 space-y-3 overflow-y-auto px-1" aria-live="polite">
        <div className="rounded-2xl bg-slate-50 px-4 py-3 text-sm text-slate-700">{L("Hai! Saya pembantu MinitAI. Tanya tentang ciri, harga atau cara bermula.", "Hi! I'm the MinitAI assistant. Ask about features, pricing or getting started.")}</div>
        {messages.length === 0 && <div className="flex flex-wrap gap-2">{chips.map((c, i) => <button key={c} onClick={() => send(c)} data-testid={`public-chat-chip-${i}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:border-violet-400 hover:text-violet-700">{c}</button>)}</div>}
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "flex justify-end" : ""} data-testid={`public-chat-message-${m.role}`}>
            <div className={`max-w-[88%] whitespace-pre-wrap rounded-2xl px-4 py-3 text-sm leading-relaxed ${m.role === "user" ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-800"}`}>
              {m.role === "assistant" && !m.content ? <Loader2 size={16} className="animate-spin text-violet-600" /> : m.role === "user" ? m.content : linkify(m.content, i)}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(q); }} className="mt-3 flex items-end gap-2 rounded-2xl border border-slate-200 p-2 focus-within:border-violet-400">
        <Textarea value={q} onChange={(e) => setQ(e.target.value)} rows={1} placeholder={L("Tulis soalan…", "Type a question…")} aria-label="Soalan"
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(q); } }}
          className="min-h-[40px] resize-none border-0 shadow-none focus-visible:ring-0" data-testid="public-chat-input" />
        <Button type="submit" size="icon" disabled={loading || !q.trim()} className="h-10 w-10 shrink-0 rounded-xl bg-violet-600 hover:bg-violet-700" data-testid="public-chat-submit" aria-label="Hantar"><ArrowUp size={18} /></Button>
      </form>
    </div>
  );
};

export const PublicChatWidget = () => {
  const { L } = useApp();
  return <ChatLauncher title={L("Tanya MinitAI", "Ask MinitAI")} testId="public-chat-widget"><PublicChat /></ChatLauncher>;
};
